import type { InjectionKey } from 'vue'
import type { Locale, TranslateOptions } from 'vue-i18n'

const collectionLocaleKey: InjectionKey<Locale> = Symbol('collection-locale')

/**
 * Makes the collection's language available to its content, so visitor-facing strings
 * render in the language the collection was written in rather than the viewer's UI locale.
 * Collections without a (supported) language default to English.
 */
export const provideCollectionLocale = async (language: string | null | undefined): Promise<void> => {
  const { $i18n } = useNuxtApp()
  const locale = $i18n.availableLocales.find(availableLocale => availableLocale === language) ?? 'en'

  // Provide before awaiting, as the component instance is no longer active afterwards
  provide(collectionLocaleKey, locale)

  await $i18n.loadLocaleMessages(locale)
}

/**
 * Translates using the collection's language.
 */
export const useCollectionI18n = () => {
  const { t } = useI18n()
  const locale = inject(collectionLocaleKey, 'en')

  return {
    t: (key: string, named: Record<string, unknown> = {}, options: TranslateOptions<Locale> = {}): string =>
      t(key, named, { ...options, locale })
  }
}
